import React from 'react';
import { Await, useLoaderData, useNavigate, useNavigation, useSubmit } from 'react-router-dom';
import { DocumentIcon } from '@heroicons/react/24/outline';

import { DataTable } from '@oysterjs/ui/data-table';
import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';

import {
  Action,
  CustomFormFieldsFragment,
  CustomFormListFieldsFragment,
  Resource,
  VisibilityConfigInput
} from '~/api/graphql';
import { PermissionButton, usePermissions } from '~/components/permissions';
import { BulkVisibilityDialog } from '~/components/visibility/bulk-visibility-dialog';
import { visibilityInputFromValue } from '~/components/visibility/visibility-selector.utils';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';

import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { CustomFormEditDialog } from '~/components/library/custom-form-edit-dialog';
import { CustomFormCreateDialog } from '~/components/library/custom-form-create-dialog';
import { FeatureDocCallout } from '~/components/library/feature-doc-callout';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';
import { LibraryEditOverlayLoader } from '~/components/library/library-edit-overlay-loader';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { LibraryListOutlet } from '~/components/library/library-list-outlet';
import { ProUpgradeDialog } from '~/components/library/pro-upgrade-dialog';
import {
  customFormRowHref,
  customFormSortFn,
  getCustomFormColumns,
  type CustomFormSortKey
} from '~/components/library/custom-forms-columns';
import {
  createCustomFormFilterDefinitions,
  filterCustomForms,
  type CustomFormFilter
} from '~/api/data/forms/custom-forms-filters';
import { useCurrentViewer } from '~/components/library/hooks/use-current-viewer';
import { useCustomFormEditState } from '~/components/library/hooks/use-custom-form-edit-state';
import { useCustomFormsContext } from '~/components/library/hooks/use-custom-forms-context';
import { useFloatingSelection } from '~/components/list/use-floating-selection-bar';
import { useLibraryBulkArchive } from '~/components/list/use-library-bulk-archive';
import { useLibraryBulkVisibility } from '~/components/library/hooks/use-library-bulk-visibility';

const ARCHIVE_PATH = '/app/library/forms/archive';
const VISIBILITY_PATH = '/app/library/forms/visibility';

const CREATE_CUSTOM_FORM = [{ resource: Resource.CustomForm, action: Action.Create }];
const UPDATE_CUSTOM_FORM = [{ resource: Resource.CustomForm, action: Action.Update }];

const VISIBILITY_NOTICE =
  'This changes only the selected custom forms. Existing workspaces and documents created from them keep their current visibility.';

/**
 * Custom forms tab of the library. Lists every uploaded PDF form; uses
 * the same router-action plumbing as the styles / templates / word-templates
 * tabs (`useLibraryBulkArchive` + `useLibraryBulkVisibility`), and the same
 * rowHref-driven row navigation. The only structural quirk is that forms in
 * Queued / Processing status are not row-clickable until the server has
 * finished ingesting them.
 */
export const LibraryFormsPage = () => {
  const loader = useLoaderData() as
    | { data?: Promise<{ customForms?: CustomFormListFieldsFragment[] }> }
    | undefined;

  const viewer = useCurrentViewer();
  const visibilityEntitled = useVisibilityEntitlement();
  const { userNameById, hasProEntitlement } = useCustomFormsContext();
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_CUSTOM_FORM);

  const { selected, setSelected, selectedSet, clear } = useFloatingSelection();

  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [showProUpgradeDialog, setShowProUpgradeDialog] = React.useState(false);

  const filterDefinitions = React.useMemo(() => createCustomFormFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<CustomFormFilter, CustomFormSortKey>(
      'cara_custom_forms_default_view',
      filterDefinitions,
      { defaultSortBy: 'created', defaultSortDirection: 'desc' }
    );

  const archive = useLibraryBulkArchive({
    actionPath: ARCHIVE_PATH,
    payloadKey: 'formIds',
    onArchived: clear
  });

  const visibility = useLibraryBulkVisibility({
    actionPath: VISIBILITY_PATH,
    payloadKey: 'formIds',
    itemPlural: 'forms',
    onUpdated: clear
  });

  const openAddForm = React.useCallback(() => {
    if (!hasProEntitlement) {
      setShowProUpgradeDialog(true);
    } else {
      setUploadDialogOpen(true);
    }
  }, [hasProEntitlement]);

  const sortFn = React.useCallback(
    (rows: CustomFormListFieldsFragment[], key: CustomFormSortKey, dir: 'asc' | 'desc') =>
      customFormSortFn(rows, key, dir, userNameById),
    [userNameById]
  );

  return (
    <div className="space-y-4">
      <FeatureDocCallout
        variant="sky"
        title="Custom forms"
        description="Upload the PDF forms Cara should be able to prepare, fillable or scanned. Cara extracts the fields and uses them inside workspaces to draft accurate, ready-to-send forms."
        docHref="https://docs.getcara.ai"
      />
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <SearchFilterInput
            filterDefinitions={filterDefinitions}
            onFiltersChange={setActiveFilters}
            initialFilters={initialFilters}
            placeholder="Filter by title, creator, or search..."
          />
        </div>
        <PermissionButton
          permissions={CREATE_CUSTOM_FORM}
          whenDenied="hide"
          color="sky"
          type="button"
          className="min-h-12"
          onClick={openAddForm}
        >
          Add form
        </PermissionButton>
      </div>
      <React.Suspense fallback={<LibraryListLoading title="Loading forms..." />}>
        <Await resolve={loader?.data}>
          {(data: { customForms?: CustomFormListFieldsFragment[] } | undefined) => {
            const allForms = data?.customForms ?? [];
            const filtered = filterCustomForms(allForms, activeFilters, userNameById, viewer);

            return (
              <div className="relative">
                {canUpdate && (
                  <FloatingSelectionBar
                    count={selected.length}
                    onClear={clear}
                    onVisibility={() => visibility.openDialog(selected)}
                    onArchive={archive.openDialog}
                    isArchiving={archive.isArchiving}
                    isUpdatingVisibility={visibility.isUpdating}
                    visibilityEnabled={visibilityEntitled}
                  />
                )}
                {allForms.length === 0 ? (
                  <LibraryListEmptyState
                    title="No saved forms yet"
                    description="Upload a PDF to add your first form to the library."
                    icon={DocumentIcon}
                    action={
                      <PermissionButton
                        permissions={CREATE_CUSTOM_FORM}
                        whenDenied="hide"
                        color="sky"
                        disabled={!hasProEntitlement}
                        onClick={openAddForm}
                      >
                        Upload your first form
                      </PermissionButton>
                    }
                  />
                ) : filtered.length === 0 ? (
                  <LibraryListEmptyState
                    title="No forms match"
                    description="Try adjusting your search or filters."
                    icon={DocumentIcon}
                  />
                ) : (
                  <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
                    <DataTable
                      data={filtered}
                      columns={getCustomFormColumns({
                        selectedSet,
                        setSelectedForms: setSelected,
                        filtered,
                        userNameById,
                        canEdit: canUpdate
                      })}
                      keyExtractor={(form) => form.id}
                      rowHref={customFormRowHref}
                      tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                      sortKey={sortBy}
                      sortDirection={sortDirection}
                      onSort={setSort}
                      sortFn={sortFn}
                    />
                  </div>
                )}
              </div>
            );
          }}
        </Await>
      </React.Suspense>

      <CustomFormCreateDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        action="/app/library/forms/new"
      />

      <ArchiveConfirmDialog
        open={archive.dialogOpen}
        count={selected.length}
        itemNoun="form"
        loading={archive.isArchiving}
        notice="Cara will no longer be able to find or use them in new workspaces."
        onCancel={archive.closeDialog}
        onConfirm={() => archive.archive(selected)}
      />

      <BulkVisibilityDialog
        open={visibility.open}
        count={visibility.ids.length}
        itemLabel="form"
        notice={VISIBILITY_NOTICE}
        resultMessage={visibility.resultMessage}
        loading={visibility.isUpdating}
        onClose={visibility.closeDialog}
        onSave={(v: VisibilityConfigInput) => visibility.save(v)}
      />

      <ProUpgradeDialog
        open={showProUpgradeDialog}
        title="Unlock Custom Forms with Pro"
        description="Upload your PDF forms and let Cara prepare and fill them automatically. Upgrade to the Pro plan to use Custom Forms and other powerful workflow features."
        onClose={() => setShowProUpgradeDialog(false)}
      />

      <LibraryListOutlet />
    </div>
  );
};

/** Edit dialog wrapper. Loaded as a child route of /app/library/forms. */
export const CustomFormEditPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ customForm?: CustomFormFieldsFragment }> }
    | undefined;
  const submit = useSubmit();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const saving = navigation.state === 'submitting' && navigation.formMethod === 'PUT';

  return (
    <React.Suspense fallback={<LibraryEditOverlayLoader />}>
      <Await resolve={loader?.data}>
        {(data: { customForm?: CustomFormFieldsFragment } | undefined) => {
          if (!data?.customForm) return null;
          return (
            <CustomFormEditDialogContainer
              customForm={data.customForm}
              navigate={navigate}
              submit={submit}
              saving={saving}
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};

const CustomFormEditDialogContainer = ({
  customForm,
  navigate,
  submit,
  saving
}: {
  customForm: CustomFormFieldsFragment;
  navigate: ReturnType<typeof useNavigate>;
  submit: ReturnType<typeof useSubmit>;
  saving: boolean;
}) => {
  const { state, patch } = useCustomFormEditState(customForm);
  const visibilityEditAllowed = useVisibilityEditAllowed(customForm.createdBy?.id);

  const handleSave = () => {
    if (!state.title.trim()) return;
    const formData = new FormData();
    formData.append('title', state.title.trim());
    formData.append('fillPrompt', state.fillPrompt);
    formData.append('ingestionGuidance', state.ingestionGuidance.trim());
    if (state.replacementFiles[0]?.upload) {
      formData.append('file', state.replacementFiles[0].upload);
    }
    if (visibilityEditAllowed) {
      formData.append('visibility', JSON.stringify(visibilityInputFromValue(state.visibility)));
    }
    submit(formData, { method: 'PUT', encType: 'multipart/form-data' });
  };

  return (
    <CustomFormEditDialog
      editState={state}
      saving={saving}
      onSave={handleSave}
      onClose={() => navigate('/app/library/forms')}
      onEditStateChange={patch}
    />
  );
};
